import { type FC, useCallback } from 'react';

import { Alert } from '@vakantiesnl/components/src/__LEGACY__/atoms/Alert';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { CarouselControl } from '@vakantiesnl/components/src/atoms/CarouselControl/CarouselControl';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { PlaceholderPriceBar, getPriceBarRating, PriceBar } from '@vakantiesnl/components/src/molecules/PriceBar';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type SearchUrl } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Search, type MicroCopy } from '@vakantiesnl/types';

import { useCustomStyles } from './TripOffers.styles';

const MOBILE_BAR_LIMIT = 5;

export type TripOffersProps = {
	offers: Search.OfferItem[] | Search.AccommodationOnlyOffer[];
	onClickDepartEarlier: (buttonLabel: string) => void;
	onClickDepartLater: (buttonLabel: string) => void;
	onSelect: (id: string) => void;
	microCopies: MicroCopy;
	selected?: string;
	earlierIsSelectable: boolean;
	laterIsSelectable: boolean;
	min: number;
	max: number;
	cheapest: string;
	noOffersLink: SearchUrl;
	onNoOffersLinkClick: VoidFunction;
	invalidPreselection?: boolean;
	/** Show total price in pricebar in stead of the price per adult */
	showTotalPrice?: boolean;
	carouselRange: number;
	isLoadingOffers: boolean;
};

export const TripOffers: FC<TripOffersProps> = ({
	offers,
	onSelect,
	onClickDepartEarlier,
	onClickDepartLater,
	earlierIsSelectable,
	laterIsSelectable,
	microCopies,
	selected,
	min,
	max,
	cheapest,
	noOffersLink,
	onNoOffersLinkClick,
	invalidPreselection,
	showTotalPrice,
	carouselRange,
	isLoadingOffers,
}) => {
	const { classes, cx } = useCustomStyles();
	const { isMobile, isDesktop } = useBreakpoints();

	const alertDescription = microCopies['accommodation.noresults.description'];
	const alertTitle = inject(microCopies['accommodation.noresults.title.template'], {
		alternativeSearchUrl: (
			<Link {...noOffersLink} className={classes.noOffersLink} onClick={onNoOffersLinkClick}>
				{microCopies['accommodation.noresults.alternativeSearchUrl']}
			</Link>
		),
	});

	const departEarlierButtonLabel = microCopies['accommodation.departEarlier'];
	const departLaterButtonLabel = microCopies['accommodation.departLater'];

	const departEarlierButtonClickHandler = useCallback(() => {
		onClickDepartEarlier(departEarlierButtonLabel);
	}, [departEarlierButtonLabel, onClickDepartEarlier]);

	const departLaterButtonClickHandler = useCallback(() => {
		onClickDepartLater(departLaterButtonLabel);
	}, [departLaterButtonLabel, onClickDepartLater]);

	const fillerOffers = Array(Math.max(carouselRange - offers.length, 0)).fill('');

	return (
		<>
			<div className={classes.container}>
				{offers.length === 0 || invalidPreselection ? (
					<Alert
						// TODO: fix me when we have new alert design
						title={alertDescription ? alertTitle : undefined}
						description={alertDescription || alertTitle}
						variant="warning"
						dataCy="accommodation-NoOffersAlert"
					/>
				) : (
					<div className={classes.pricebarSlider}>
						{isDesktop && (
							<CarouselControl
								icon="arrowLeft"
								title={departEarlierButtonLabel}
								onClick={departEarlierButtonClickHandler}
								disabled={isLoadingOffers || !earlierIsSelectable}
								className={cx(classes.controlButton, { [classes.hiddenButton]: !earlierIsSelectable })}
							/>
						)}
						<ul className={classes.priceBarContainer}>
							{offers.map((offer, key) => {
								if (isMobile && key >= MOBILE_BAR_LIMIT) {
									return;
								}

								const priceBarRating = getPriceBarRating(offer.priceTotal, max, min);
								return (
									<PriceBar
										departureDate={offer.departureDateYYYYMMDD}
										price={showTotalPrice ? offer.priceTotal : offer.pricePerAdult}
										durationDays={offer.durationDays}
										durationNights={offer.durationNights}
										onPriceBarClick={onSelect}
										selected={offer.id === selected}
										lowest={offer.id === cheapest}
										rating={priceBarRating}
										key={offer.id}
										id={offer.id}
										microCopies={microCopies}
										hasLuggageIncluded={
											'hasLuggageIncluded' in offer ? offer.hasLuggageIncluded : false
										}
										hasTransfer={
											'rooms' in offer ? offer.rooms.some((room) => room.hasTransfer) : false
										}
									/>
								);
							})}
							{fillerOffers.map((_, i) => (
								<PlaceholderPriceBar isInvisible={!isLoadingOffers} key={i} />
							))}
						</ul>
						{isDesktop && (
							<CarouselControl
								icon="arrowRight"
								title={departLaterButtonLabel}
								onClick={departLaterButtonClickHandler}
								className={cx(classes.controlButton, { [classes.hiddenButton]: !laterIsSelectable })}
								disabled={isLoadingOffers || !laterIsSelectable}
							/>
						)}
						{!isDesktop && (
							<div className={classes.swiftDepartureDates}>
								<>
									<Button
										leadingIcon="chevronLeft"
										variant="tertiary"
										size="small"
										onClick={departEarlierButtonClickHandler}
										disabled={isLoadingOffers || !earlierIsSelectable}
										className={cx({ [classes.hiddenButton]: !earlierIsSelectable })}
									>
										{microCopies['common.earlier']}
									</Button>
									<Button
										trailingIcon="chevronRight"
										variant="tertiary"
										size="small"
										onClick={departLaterButtonClickHandler}
										disabled={isLoadingOffers || !laterIsSelectable}
										className={cx({ [classes.hiddenButton]: !laterIsSelectable })}
									>
										{microCopies['common.later']}
									</Button>
								</>
							</div>
						)}
					</div>
				)}
			</div>

			{offers.length > 0 && !invalidPreselection && (
				<NoMoreOffers previousAvailable={earlierIsSelectable} nextAvailable={laterIsSelectable} />
			)}
		</>
	);
};

const NoMoreOffers: FC<{ previousAvailable: boolean; nextAvailable: boolean }> = ({
	previousAvailable,
	nextAvailable,
}) => {
	const microCopies = useMicroCopyContext();

	if (!previousAvailable && !nextAvailable) {
		return <Alert variant="info" description={microCopies['accommodation.noMorePreviousNextOffers']} />;
	}

	if (!previousAvailable) {
		return <Alert variant="info" description={microCopies['accommodation.noMorePreviousOffers']} />;
	}

	if (!nextAvailable) {
		return <Alert variant="info" description={microCopies['accommodation.noMoreNextOffers']} />;
	}

	return null;
};
